import useSWR from 'swr'
import { MSPUser, MSP_USER_ROLE, LicenseScope } from '@saasalerts/common/shared'
import useUser from '@/hooks/useUser'
import { useEffect } from 'react'
import { LIST_OF_REPORTS } from '@/utils/constants'
import { useLicenseScope } from '@/contexts/LicenseScopeContext'
import { withMeticulous } from '@/utils/meticulous'

export function isReportAllowedForScope(
  reportHref: string,
  scope: LicenseScope
): boolean {
  const report = LIST_OF_REPORTS.find((r) => reportHref.endsWith(r.href))
  if (!report?.domainScope) return true
  return report.domainScope.includes(scope)
}

export function useDomainAllowed(pathname: string): boolean {
  const scope = useLicenseScope()
  return isReportAllowedForScope(pathname, scope)
}

export default function useAuthorization(): {
  isAllowed: (pathname: string) => boolean
  loading: boolean
  data?: MSPUser
} {
  const { logout } = useUser()
  const { data, error } = useSWR<MSPUser>('/api/msp-user', {
    revalidateOnFocus: true,
  })
  const licenseScope = useLicenseScope()

  useEffect(() => {
    if (data && data.status !== 'active') {
      logout()
    }

    if (data) {
      void withMeticulous((meticulous) => {
        meticulous.context.recordUserId(String(data.id ?? data.partnerId))
        meticulous.context.recordCustomContext('userRole', data.role)
        meticulous.context.recordCustomContext('isMSPAdmin', data.isMSPAdmin)
        meticulous.context.recordCustomContext(
          'partnerId',
          String(data.partnerId)
        )
        if (data.status) {
          meticulous.context.recordCustomContext('status', data.status)
        }
        if (data.licenseScopes?.length) {
          meticulous.context.recordCustomContext(
            'licenseScopes',
            data.licenseScopes.join(',')
          )
        }
      })
    }
  }, [data])
  /*
   * We only want to error out when the error is not 401.
   * 401 is thrown when the user is not logged in and it will happen when
   * the user is on the login page.
   */
  if (!data && (!error || error.status === 401)) {
    return {
      loading: true,
      isAllowed: () => false,
    }
  }
  const USER_RESTRICTIONS = {
    [MSP_USER_ROLE.MSP_USER]: [
      '/settings',
      '/scheduled-reports',
      '/reports/billing-details',
      '/reports/psa-organization-mapping',
    ],
    [MSP_USER_ROLE.MSP_ADMIN]: [],
  }

  const userRole = data?.role
  if (userRole === undefined) {
    return {
      loading: false,
      isAllowed: () => false,
    }
  }
  const restrictions = USER_RESTRICTIONS[userRole]

  if (restrictions === undefined) {
    return {
      loading: false,
      isAllowed: () => false,
    }
  }

  const isAllowed = (pathname: string) =>
    restrictions.find(
      (restrictedPath: string) => restrictedPath === pathname
    ) === undefined && isReportAllowedForScope(pathname, licenseScope)

  return {
    loading: false,
    isAllowed,
    data,
  }
}
